import {AiResultContainer, CornerLabel, FCX, getRem} from "@datassential/platform-ui-lib";
import AiResultQuestion from "@vms/AiResult/AiResultQuestion";
import { ComponentProps, ReactNode, useMemo } from "react";
import AiResultFeedback from "@vms/AiResult/AiResultFeedback";
import AiResultSettings, { SettingsItem } from "@vms/AiResult/AiResultSettings";
import cn from "classnames";
import Skeleton from "@components/Skeleton";

const AiResult: FCX<{
    question: string;
    answer?: string;
    answerNode?: ReactNode;
    isAnswerLoading?: boolean;
    settings: SettingsItem[][];
    isSettingsLoading: boolean;
}> = ({
    question,
    answer: rawAnswer,
    answerNode,
    className,
    isAnswerLoading = false,
    settings: rawSettings,
    isSettingsLoading = false,
}) => {
    const settingsSkeleton = useMemo(
        (): SettingsItem[][] => {
            const titleSkeleton = <Skeleton width={120} height={20}/>;
            const valueSkeleton = <Skeleton width={160} height={20} style={{ marginTop: getRem(6) }}/>;
            return [
                new Array(2).fill(null).map((_, index) => ({
                    id: `skeleton_1_${index}`,
                    title: titleSkeleton,
                    value: valueSkeleton,
                    isLoader: true,
                })),
                new Array(2).fill(null).map((_, index) => ({
                    id: `skeleton_2_${index}`,
                    title: titleSkeleton,
                    value: valueSkeleton,
                    isLoader: true,
                })),
            ];
        },
        []
    );

    const settings = useMemo(
        () => {
            if (isSettingsLoading) return settingsSkeleton;

            return rawSettings;
        },
        [isSettingsLoading, settingsSkeleton, rawSettings]
    );

    const answerSkeleton = useMemo(
        () => {
            return (
                <>
                    <Skeleton height={44}/>
                    <Skeleton height={44}/>
                </>
            );
        },
        []
    );

    const resultFeedback = useMemo(
        () => {
            if (isAnswerLoading) {
                return <Skeleton width={200} height={40}/>;
            }

            return <AiResultFeedback className="appears"/>;
        },
        [isAnswerLoading]
    );

    return (
        <AiResultContainer className={cn("AiResult", className)}>
            <div className="AiResult__result-container">
                <AiResultQuestion className="appears" question={question}/>
                <div className="AiResult__result">
                    {isAnswerLoading && answerSkeleton}
                    {!isAnswerLoading && !!rawAnswer && (
                        <div dangerouslySetInnerHTML={{ __html: rawAnswer }} />
                    )}
                    {!isAnswerLoading && !rawAnswer && answerNode}
                </div>
                {resultFeedback}
            </div>
            <CornerLabel modifiers={["top", "left"]}>
                BETA
            </CornerLabel>
            <AiResultSettings settingsColumns={settings}/>
        </AiResultContainer>
    );
};

export type AiResultProps = ComponentProps<typeof AiResult>;

export default AiResult;